<script lang="ts" setup>
import { getDisplay, getValue } from "@/DataHelper";
import {
  EditComboboxItemControlProps,
  EditComboBoxItemUiData,
  iconFav,
  iconNoFav,
} from "./EditComboboxFunctions";
import {
  CommonEditControlEmits,
  getDefaultEditorControlConfig,
} from "./EditComponentStatic";
import { onMounted, onUpdated, reactive } from "vue";

const emitFn = defineEmits<
  CommonEditControlEmits & {
    (e: "changeFav", value: { val: number; aic?: number }): void;
  }
>();

const props = defineProps<
  EditComboboxItemControlProps & { overlayWidth?: number }
>();
const { config, ui } = getDefaultEditorControlConfig<
  EditorControlConfig,
  EditComboboxItemControlProps,
  EditComboBoxItemUiData
>(props, emitFn);

var uiData = reactive(ui);

// Ref für die ddItem-Row, um die Overlay-Breite zu messen
import {
  ref as vueRef,
  onMounted as vueOnMounted,
  nextTick as vueNextTick,
  computed,
} from "vue";
const rowRef = vueRef<HTMLElement | null>(null);
// overlayWidth kommt jetzt als Prop von außen

onMounted(updateMe);
onUpdated(updateMe);

function getCellStyle(ix: number | string): string {
  // Wenn Prozentwert und Containerbreite bekannt, rechne um
  let localMaxWidth = props.overlayWidth;
  if (localMaxWidth === 0) {
    localMaxWidth = 500;
  }
  if (typeof ix === "number" && props.favColumn) {
    const headerLength = Array.isArray(props.item?.row)
      ? props.item.row.length
      : 0;
    const dataLength = props.visibleColumns?.length ?? 0;
    const lastIndex = props.item?.isHeader ? headerLength - 1 : dataLength - 1;
    if (
      ix === lastIndex &&
      lastIndex >= 0 &&
      localMaxWidth &&
      props.item.percent
    ) {
      const px = Math.round((props.item.percent / 100) * localMaxWidth);
      return `width: ${px}px; min-width: ${px}px;`;
    }
  }

  let col: DataColumn | undefined;
  if (typeof ix === "string") {
    col = props.visibleColumns?.find((c) => c.name == ix);
  } else {
    col = props.visibleColumns![ix];
  }
  const w = col?.meta?.webWidth ?? col?.meta?.width;
  if (w) {
    return `width: ${w}px; min-width: ${w}px;`;
  }
  // Fallback: Prozentwert, falls rowWidth noch nicht bekannt
  if (props.item.percent) {
    return `width: ${props.item.percent ?? 50}%;`;
  }
  return "";
}

function updateMe() {
  if (props.item) {
    uiData.description = props.item.description ?? "";
    uiData.rowData = props.item.row;
    uiData.percent = props.item.percent;
  }

  if (props.item.row) {
    uiData.rowData = props.item.row;
  }

  if (props.favColumn && uiData.rowData) {
    uiData.favVal = getValue(uiData.rowData, props.favColumn);
  }
}

function clickHeader(e: any) {
  if (props.item.isHeader) {
    e.cancelBubble = true;
    return false;
  }
}
function toggleFav(e: any) {
  e.cancelBubble = true;
  if (uiData.favVal === 2) {
    console.log("cannot update global favorite");
    return;
  } else if (uiData.favVal === 1) {
    uiData.favVal = 0;
  } else if (uiData.favVal === 0) {
    uiData.favVal = 1;
  }

  const favColumnName: string = props.favColumn!.name;
  if (uiData.rowData) {
    uiData.rowData[favColumnName] = uiData.favVal;
    let aic = uiData.rowData.aic_Stamm;
    if (!aic) {
      aic = uiData.rowData.aic;
    }
  }

  emitFn("changeFav", { val: uiData.favVal, aic: props.item.aic });

  // nextTick(() => {
  //   vueInstance?.update();
  // });
}
</script>
<template>
  <div @click="clickHeader" class="pa-2">
    <div v-if="item.isHeader" class="ddHeader">
      <span
        v-for="(r, ix) in item.row"
        :key="'di' + ix"
        class="ddItemCell ddHeaderCell"
        :style="getCellStyle(ix)"
        v-html="r"
      >
      </span>
      <span
        v-if="props.favColumn"
        class="ddItemCell ddItemCellFav"
        @click="toggleFav"
      >
        <v-icon :icon="iconFav" dense color="black" />
      </span>
    </div>
    <div v-else-if="!uiData.rowData || uiData.rowData.length == 0">
      {{ uiData.description }}
    </div>
    <div
      v-else
      class="ddItem"
      :title="uiData.description"
      style="width: 100%"
      ref="rowRef"
    >
      <span
        v-for="(col, ix) in visibleColumns"
        :key="'di' + ix"
        class="ddItemCell"
        :style="getCellStyle(ix)"
      >
        {{ getDisplay(uiData.rowData, col) }}
      </span>
      <span
        v-if="props.favColumn"
        class="ddItemCell ddItemCellFav"
        @click="toggleFav"
      >
        <v-icon
          :icon="iconFav"
          v-if="uiData.favVal === 2"
          dense
          color="green"
        />
        <v-icon
          :icon="iconFav"
          v-if="uiData.favVal === 1"
          dense
          color="black"
        />
        <v-icon
          :icon="iconNoFav"
          v-if="uiData.favVal === 0"
          dense
          color="black"
        />
      </span>
    </div>
  </div>
</template>
<style>
.ddHeader,
.ddItem {
  width: 100%;
  margin: auto;
  text-overflow: ellipsis;
  overflow: hidden;
}

.ddHeader {
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  font-weight: bold;
  text-align: left;
  height: 24px;
  padding: 0;
  font-size: 12px !important;

  font-family:
    Roboto,
    "Segoe UI",
    GeezaPro,
    "DejaVu Serif",
    sans-serif,
    -apple-system,
    BlinkMacSystemFont;
  border-bottom: 2px solid black;
}
.ddItem {
  display: flex; /* sorgt dafür, dass alle Zellen nebeneinander bleiben */
  align-items: center; /* vertikal mittig */
  white-space: nowrap; /* verhindert Zeilenumbruch */
  overflow: hidden; /* falls der Inhalt zu lang ist */
  text-overflow: ellipsis; /* optional: "…" am Ende */
}
.ddItemCell {
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: none;
  white-space: nowrap;
  line-break: initial;
  text-align: left;
  padding: 0;
}

.ddHeaderCell {
  font-weight: bold;
}

.ddItemCellFav {
  cursor: pointer;
  text-align: right;
  margin-left: auto;
  flex: 0 0 32px;
  width: 32px;
  min-width: 32px;
  max-width: 32px;
  display: inline-flex;
  justify-content: flex-end;
  align-items: center;
  padding-left: 0;
}
</style>
